// Türkiye resmi tatilleri — herhangi bir yıl için DİNAMİK olarak hesaplanır.
//
// • Sabit (milli/resmi) tatiller doğrudan Gregoryen takvimden alınır.
// • Dini bayramlar (Ramazan / Kurban) ay takvimine bağlı olduğundan
//   her yıl yaklaşık 11 gün kayar. Bunlar Hicri takvim (Umm al-Qura)
//   üzerinden ilgili Gregoryen yıla yansıtılarak otomatik bulunur.
//
// type:
//   'full' → tam gün resmi tatil (izin süresinden hiç düşülmez)
//   'half' → arefe / yarım gün (en fazla 0,5 gün sayılır)

const pad = (n) => String(n).padStart(2, '0');

// Bir Date nesnesini yerel takvime göre 'YYYY-MM-DD' anahtarına çevirir.
export const toDateKey = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Sabit tarihli resmi tatiller (her yıl aynı gün).
const FIXED_HOLIDAYS = [
  { month: 1, day: 1, name: 'Yılbaşı', type: 'full' },
  { month: 4, day: 23, name: 'Ulusal Egemenlik ve Çocuk Bayramı', type: 'full' },
  { month: 5, day: 1, name: 'Emek ve Dayanışma Günü', type: 'full' },
  { month: 5, day: 19, name: "Atatürk'ü Anma, Gençlik ve Spor Bayramı", type: 'full' },
  { month: 7, day: 15, name: 'Demokrasi ve Milli Birlik Günü', type: 'full' },
  { month: 8, day: 30, name: 'Zafer Bayramı', type: 'full' },
  { month: 10, day: 28, name: 'Cumhuriyet Bayramı Arefesi', type: 'half' },
  { month: 10, day: 29, name: 'Cumhuriyet Bayramı', type: 'full' },
];

// Hicri (Umm al-Qura) ay/gün bilgisini döndürür. Tek formatter, tembel kurulur.
let hijriFormatter = null;
const getHijriParts = (date) => {
  if (!hijriFormatter) {
    hijriFormatter = new Intl.DateTimeFormat('en-US-u-ca-islamic-umalqura', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
    });
  }
  const parts = hijriFormatter.formatToParts(date);
  const get = (type) => Number(parts.find((p) => p.type === type)?.value);
  return { day: get('day'), month: get('month'), year: get('year') };
};

const yearCache = new Map();

// Belirli bir yılın tüm resmi tatillerini Map<'YYYY-MM-DD', {date,name,type}> döndürür.
export const getTurkishHolidaysForYear = (year) => {
  if (yearCache.has(year)) return yearCache.get(year);

  const map = new Map();
  const add = (date, name, type) => {
    const key = toDateKey(date);
    // Aynı güne birden fazla tatil düşerse ilk (önceliklisi) kalır.
    if (!map.has(key)) map.set(key, { date: key, name, type });
  };

  // 1) Sabit tatiller
  FIXED_HOLIDAYS.forEach((h) => add(new Date(year, h.month - 1, h.day), h.name, h.type));

  // 2) Dini bayramlar — yıl içindeki her günü tarayıp Hicri takvimden tespit et.
  const cursor = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);
  while (cursor <= end) {
    const { month, day } = getHijriParts(cursor);

    // Ramazan Bayramı: 1–3 Şevval (Hicri ay 10)
    if (month === 10 && day >= 1 && day <= 3) {
      add(new Date(cursor), `Ramazan Bayramı ${day}. Gün`, 'full');
      if (day === 1) {
        const arefe = new Date(cursor);
        arefe.setDate(arefe.getDate() - 1);
        add(arefe, 'Ramazan Bayramı Arefesi', 'half');
      }
    }

    // Kurban Bayramı: 10–13 Zilhicce (Hicri ay 12)
    if (month === 12 && day >= 10 && day <= 13) {
      add(new Date(cursor), `Kurban Bayramı ${day - 9}. Gün`, 'full');
      if (day === 10) {
        const arefe = new Date(cursor);
        arefe.setDate(arefe.getDate() - 1);
        add(arefe, 'Kurban Bayramı Arefesi', 'half');
      }
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  yearCache.set(year, map);
  return map;
};

// Bir tarih aralığını kapsayan (birden fazla yıla yayılabilir) tek bir tatil haritası.
export const getHolidayMapForRange = (start, end) => {
  const map = new Map();
  for (let y = start.getFullYear(); y <= end.getFullYear(); y += 1) {
    getTurkishHolidaysForYear(y).forEach((value, key) => map.set(key, value));
  }
  return map;
};

// Verilen aralığa düşen resmi tatilleri (sıralı) döndürür — UI'da listelemek için.
export const getHolidaysInRange = (startDate, endDate) => {
  if (!startDate || !endDate) return [];
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return [];

  const map = getHolidayMapForRange(start, end);
  const result = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const hit = map.get(toDateKey(cursor));
    if (hit) result.push(hit);
    cursor.setDate(cursor.getDate() + 1);
  }
  return result;
};
